import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { CorrelationGraph, GraphEdge, GraphNode } from '../api/types'
import { formatBytes } from '../components/charts'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { leggiLayout, scriviLayout, type LayoutMappa, type Posizione } from '../components/mappaLayout'
import Mappa, { type ArcoMappa, type NodoMappa, type VoceLegenda } from '../components/Mappa'
import { t } from '../i18n/lingua'
import ComposizioneDaIndirizzo from './FlowComposition'

/**
 * Mappa di correlazione: si parte da un indirizzo e si allarga.
 *
 * È la forma in cui si fanno davvero le domande a cui il traffico serve a
 * rispondere — «questo indirizzo sconosciuto con chi parla?», «chi usa questo
 * servizio?», «se spengo questa macchina, chi se ne accorge?» — e nessuna delle
 * tre si risponde con un elenco: serve vedere il vicinato, e poi il vicinato del
 * vicinato.
 *
 * ## Il layout è radiale per livello, non a forza
 *
 * Il seme al centro, i vicini diretti sul primo anello, i loro vicini sul
 * secondo. Un layout a forze produce disposizioni più belle e **diverse a ogni
 * caricamento**: su una mappa che si consulta per capire una rete, non poter
 * ritrovare un nodo dove l'avevi lasciato è un costo vero. Qui la posizione di un
 * nodo dipende solo dal suo livello e dal suo ordine, quindi è stabile.
 *
 * ## Che cosa la mappa dichiara di non mostrare
 *
 * Un host attivo ha centinaia di interlocutori: al secondo livello sarebbero
 * decine di migliaia di nodi. Si prendono i più voluminosi, e i nodi che hanno
 * altro da mostrare portano un segno — perché una mappa che tronca in silenzio non
 * è incompleta, è **falsa**: mostra un vicinato che sembra tutto il vicinato.
 */

const WINDOWS = [
  { value: '1h', label: () => t('Ultima ora') },
  { value: '24h', label: () => t('Ultime 24 ore') },
  { value: '7d', label: () => t('Ultimi 7 giorni') },
]

const DIRECTIONS = [
  {
    value: 'both',
    label: () => t('Entrambi i versi'),
    hint: () => t('Tutto ciò che tocca il nodo.'),
  },
  {
    value: 'in',
    label: () => t('Chi ha parlato al nodo'),
    hint: () => t('I suoi client: chi apre verso di lui.'),
  },
  {
    value: 'out',
    label: () => t('Con chi ha parlato il nodo'),
    hint: () => t('Le sue dipendenze: verso chi apre lui.'),
  },
]

/**
 * La chiave di una comunicazione: sorgente, destinazione e porta.
 *
 * È l'identità con cui una comunicazione si esclude e si seleziona. La porta ne
 * fa parte: fra le stesse due macchine passano più cose, e escludere «il
 * backup sulla 445» non deve escludere anche «i log sulla 514».
 */
function chiaveArco(e: { src: string; dst: string; dst_port: number }): string {
  return `${e.src}→${e.dst}:${e.dst_port}`
}

/** Colore per livello: il centro si deve riconoscere senza leggere. */
function levelFill(level: number, known: boolean): string {
  if (level === 0) return 'var(--accent)'
  if (!known) return 'var(--warn)'
  return level === 1 ? 'var(--ok)' : 'var(--text-muted)'
}

/** Lo spessore di una comunicazione: il volume, in scala logaritmica — da
 *  un kilobyte a decine di gigabyte ci sono sette ordini di grandezza, e in
 *  scala lineare si vedrebbe un solo arco grosso e tutti gli altri a zero. */
function spessorePer(bytes: number): number {
  if (!bytes || bytes <= 0) return 1.2
  return Math.min(5, Math.max(1.2, 1.2 + (Math.log10(bytes) - 3) * 0.55))
}

export default function FlowMap() {
  const [seed, setSeed] = useState('')
  const [params, setParams] = useSearchParams()
  // Due domande, due viste: «com'è fatto il servizio dietro questo ingresso»
  // (la composizione, che è quello che si chiede arrivando da Entry point) e
  // «con chi parla questo indirizzo» (il vicinato, per esplorare).
  const vista: 'composizione' | 'vicinato' = params.get('vista') === 'vicinato' ? 'vicinato' : 'composizione'
  const cambiaVista = (v: 'composizione' | 'vicinato') => {
    const dopo = new URLSearchParams(params)
    dopo.set('vista', v)
    setParams(dopo, { replace: true })
  }
  const [query, setQuery] = useState<{
    address: string
    window: string
    levels: number
    fanout: number
    direction: string
  } | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  // La vista scelta si ricorda: chi lavora a colonne ci torna a colonne.
  const [layout, setLayout] = useState<LayoutMappa>(leggiLayout())

  // Gli indirizzi tolti dalla mappa. Si azzerano a ogni nuova esplorazione:
  // l'esclusione è un giudizio su **questa** mappa — «il DNS non c'entra con il
  // servizio che sto ricostruendo» — non una preferenza da ricordare.
  const [esclusi, setEsclusi] = useState<Set<string>>(new Set())


  // Le comunicazioni escluse, per chiave `src→dst:porta`. Come per i nodi:
  // l'esclusione è un giudizio su questa mappa e si azzera esplorando altrove.
  const [esclusiArchi, setEsclusiArchi] = useState<Set<string>>(new Set())

  // La comunicazione scelta: evidenziata sulla mappa E nella tabella sotto.
  // Uno stato solo per le due viste, così non possono divergere.
  const [selEdge, setSelEdge] = useState<string | null>(null)

  // Le comunicazioni spuntate nella tabella, per escluderle in blocco.
  const [spuntate, setSpuntate] = useState<Set<string>>(new Set())

  // Il menu del tasto destro: cosa è stato premuto, e dove disegnarlo.
  // Coordinate dello schermo (`position: fixed`): il menu deve comparire sotto
  // il puntatore anche con la mappa ingrandita o la pagina scorsa.
  const [menu, setMenu] = useState<{
    x: number
    y: number
    kind: 'nodo' | 'arco'
    chiave: string
  } | null>(null)

  // Il menu si chiude con un clic ovunque o con Esc: un menu contestuale che
  // resta aperto dopo che si è guardato altrove è un errore da inseguire.
  useEffect(() => {
    if (!menu) return
    const chiudi = () => setMenu(null)
    const tasto = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null)
    }
    window.addEventListener('click', chiudi)
    window.addEventListener('keydown', tasto)
    return () => {
      window.removeEventListener('click', chiudi)
      window.removeEventListener('keydown', tasto)
    }
  }, [menu])

  // I nodi trascinati a mano, come nella topologia: la disposizione radiale
  // resta la base — è stabile fra un caricamento e l'altro — ma un nodo si può
  // prendere e mettere dove serve per leggere. Si azzera a ogni esplorazione.
  const [spostati, setSpostati] = useState<Map<string, Posizione>>(new Map())

  const [window_, setWindow] = useState('24h')
  const [levels, setLevels] = useState(1)
  const [fanout, setFanout] = useState(10)
  const [direction, setDirection] = useState('both')

  const graph = useAsync<CorrelationGraph | null>(
    () =>
      query
        ? api.correlationGraph(query.address, {
            window: query.window,
            levels: query.levels,
            fanout: query.fanout,
            direction: query.direction,
          })
        : Promise.resolve(null),
    [query],
  )

  // La mappa che si vede: senza gli esclusi, e senza gli archi che li
  // toccavano. Il layout si ricalcola sui superstiti — escludere non lascia
  // buchi, ricompone — ed è questo che trasforma una mappa esplorativa nella
  // fotografia finale di un servizio.
  const visibili = useMemo(() => {
    const candidati = (graph.data?.nodes ?? []).filter((n) => !esclusi.has(n.address))
    const ammessi = new Set(candidati.map((n) => n.address))
    const edges = (graph.data?.edges ?? []).filter(
      (e) =>
        ammessi.has(e.src) && ammessi.has(e.dst) && !esclusiArchi.has(chiaveArco(e)),
    )
    // La potatura: un nodo rimasto senza comunicazioni non racconta più
    // niente — galleggerebbe scollegato, e nel servizio creato dalla mappa
    // diventerebbe un ingresso che non c'entra. Il seme resta comunque: la
    // mappa è «attorno a lui» anche quando si è escluso tutto.
    const connessi = new Set<string>([graph.data?.seed ?? ''])
    for (const e of edges) {
      connessi.add(e.src)
      connessi.add(e.dst)
    }
    const nodes = candidati.filter((n) => connessi.has(n.address))
    return { nodes, edges }
  }, [graph.data, esclusi, esclusiArchi])

  const nodiMappa = useMemo<NodoMappa[]>(
    () =>
      visibili.nodes.map((n: GraphNode) => ({
        id: n.address,
        label: n.name ?? n.address,
        sotto: n.name ? n.address : null,
        colore: levelFill(n.level, Boolean(n.name)),
        gruppo: n.level === 0 ? 'server' : n.name ? 'server' : 'esterno',
        livello: n.level,
        seme: n.level === 0,
        segno: n.has_more ? '⁺' : undefined,
      })),
    [visibili.nodes],
  )
  const archiMappa = useMemo<ArcoMappa[]>(
    () =>
      visibili.edges.map((e: GraphEdge) => ({
        id: chiaveArco(e),
        src: e.src,
        dst: e.dst,
        colore: '#3b82f6',
        spessore: spessorePer(e.bytes),
        etichetta: String(e.dst_port),
        titolo: `${e.src} → ${e.dst} · ${e.dst_port} · ${formatBytes(e.bytes)}`,
      })),
    [visibili.edges],
  )
  const legendaMappa: VoceLegenda[] = [
    { etichetta: t('Seme'), colore: 'var(--accent)', forma: 'punto' },
    { etichetta: t('Nella CMDB'), colore: 'var(--ok)', forma: 'punto' },
    { etichetta: t('Non censito'), colore: 'var(--warn)', forma: 'punto' },
    { etichetta: t('Comunicazione (spessore = volume)'), colore: '#3b82f6' },
  ]
  const scelta = visibili.edges.find((e) => chiaveArco(e) === selEdge)
  const inquadra = scelta ? [scelta.src, scelta.dst] : undefined

  function explore(address: string) {
    setSeed(address)
    setSelected(null)
    setSelEdge(null)
    setEsclusi(new Set())
    setEsclusiArchi(new Set())
    setSpuntate(new Set())
    setMenu(null)
    setSpostati(new Map())
    setQuery({ address, window: window_, levels, fanout, direction })
  }

  function escludi(address: string) {
    // Il seme no: una mappa «attorno a X» senza X non vuol dire niente.
    if (address === graph.data?.seed) return
    setEsclusi((prima) => new Set([...prima, address]))
    setSelected((sel) => (sel === address ? null : sel))
  }

  function escludiArco(chiave: string) {
    setEsclusiArchi((prima) => new Set([...prima, chiave]))
    setSelEdge((sel) => (sel === chiave ? null : sel))
  }

  function escludiSpuntate() {
    setEsclusiArchi((prima) => new Set([...prima, ...spuntate]))
    setSelEdge((sel) => (sel && spuntate.has(sel) ? null : sel))
    setSpuntate(new Set())
  }

  function ripristinaArco(chiave: string) {
    setEsclusiArchi((prima) => {
      const dopo = new Set(prima)
      dopo.delete(chiave)
      return dopo
    })
  }

  function ripristina(address: string) {
    setEsclusi((prima) => {
      const dopo = new Set(prima)
      dopo.delete(address)
      return dopo
    })
  }

  // L'indirizzo nell'URL parte da solo: è così che «apri la mappa da questo
  // entry point» diventa un collegamento e non un'istruzione. Solo al primo
  // montaggio — dopo, comanda la persona.
  useEffect(() => {
    const iniziale = params.get('seed')?.trim()
    if (iniziale && vista === 'vicinato') explore(iniziale)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const data = graph.data
  const detail =
    selected && !esclusi.has(selected)
      ? visibili.nodes.find((n) => n.address === selected)
      : null
  const detailEdges = selected
    ? visibili.edges.filter((e) => e.src === selected || e.dst === selected)
    : []

  // The name of an address on the map, or the address itself.
  const nomeNodo = (addr: string) => data?.nodes.find((n) => n.address === addr)?.name ?? addr
  const pulsanteEscludi = (chiave: string) => (
    <button
      type="button"
      className="secondary"
      onClick={(e) => {
        e.stopPropagation()
        escludiArco(chiave)
      }}
    >
      {t('Escludi')}
    </button>
  )
  const colonneComunicazioni: Column<GraphEdge>[] = [
    {
      key: 'src',
      header: t('Da'),
      value: (edge) => nomeNodo(edge.src),
      render: (edge) => (
        <button
          type="button"
          className="linklike"
          onClick={(e) => {
            e.stopPropagation()
            setSelected(edge.src)
          }}
        >
          {nomeNodo(edge.src)}
        </button>
      ),
    },
    {
      key: 'dst',
      header: t('A'),
      value: (edge) => nomeNodo(edge.dst),
      render: (edge) => (
        <button
          type="button"
          className="linklike"
          onClick={(e) => {
            e.stopPropagation()
            setSelected(edge.dst)
          }}
        >
          {nomeNodo(edge.dst)}
        </button>
      ),
    },
    { key: 'dst_port', header: t('Porta'), mono: true },
    { key: 'bytes', header: t('Volume'), type: 'number', render: (edge) => formatBytes(edge.bytes) },
    { key: 'flows', header: t('Flussi'), type: 'number' },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (edge) => pulsanteEscludi(chiaveArco(edge)) },
  ]
  // The peer of an edge as seen from the chosen node.
  const verso = (edge: GraphEdge) => edge.src === detail?.address
  const interlocutore = (edge: GraphEdge) => (verso(edge) ? edge.dst : edge.src)
  const colonneDettaglio: Column<GraphEdge>[] = [
    { key: 'verso', header: t('Verso'), value: (edge) => (verso(edge) ? t('→ verso') : t('← da')) },
    {
      key: 'peer',
      header: t('Interlocutore'),
      value: (edge) => nomeNodo(interlocutore(edge)),
      render: (edge) => (
        <button
          type="button"
          className="linklike"
          onClick={() => setSelected(interlocutore(edge))}
          onDoubleClick={() => explore(interlocutore(edge))}
        >
          {nomeNodo(interlocutore(edge))}
        </button>
      ),
    },
    { key: 'dst_port', header: t('Porta'), mono: true },
    { key: 'bytes', header: t('Volume'), type: 'number', render: (edge) => formatBytes(edge.bytes) },
    { key: 'flows', header: t('Flussi'), type: 'number' },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (edge) => pulsanteEscludi(chiaveArco(edge)) },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Mappa di correlazione')}</h1>
          <p className="page__subtitle">
            {vista === 'composizione'
              ? t('partendo da un punto di ingresso, mostra la sequenza delle chiamate tra i componenti e, per ciascuno, dove viene eseguito.')
              : t('Da un indirizzo: chi gli ha parlato, con chi ha parlato, e il vicinato dei suoi vicini')}
          </p>
        </div>
      </header>

      <div className="toolbar" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={vista === 'composizione' ? '' : 'secondary'}
          onClick={() => cambiaVista('composizione')}
        >
          {t('Composizione del servizio')}
        </button>
        <button
          type="button"
          className={vista === 'vicinato' ? '' : 'secondary'}
          onClick={() => cambiaVista('vicinato')}
        >
          {t('Esplora il vicinato')}
        </button>
      </div>

      {vista === 'composizione' ? (
        <ComposizioneDaIndirizzo seedIniziale={params.get('seed') ?? ''} />
      ) : (
      <>
      <Card title={t('Da dove partire')}>
        <div className="row">
          <div className="field">
            <label htmlFor="map-seed">{t('Indirizzo')}</label>
            <input
              id="map-seed"
              value={seed}
              placeholder={t('es. {esempio}', { esempio: '10.2.2.2' })}
              onChange={(e) => setSeed(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && seed.trim()) explore(seed.trim())
              }}
            />
            <p className="field__hint">
              {t('Un indirizzo, non un nome: nei flussi i nomi non compaiono, e cercarli darebbe sempre un risultato vuoto — che si legge come «non parla con nessuno».')}
            </p>
          </div>

          <div className="field" style={{ maxWidth: 220 }}>
            <label htmlFor="map-direction">{t('Verso')}</label>
            <select
              id="map-direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
            >
              {DIRECTIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label()}
                </option>
              ))}
            </select>
            <p className="field__hint">
              {DIRECTIONS.find((d) => d.value === direction)?.hint()}
            </p>
          </div>

          <div className="field" style={{ maxWidth: 160 }}>
            <label htmlFor="map-levels">{t('Livelli')}</label>
            <select
              id="map-levels"
              value={levels}
              onChange={(e) => setLevels(Number(e.target.value))}
            >
              <option value={1}>{t('1 — i vicini')}</option>
              <option value={2}>{t('2 — e i loro')}</option>
              <option value={3}>{t('3 — oltre')}</option>
            </select>
            <p className="field__hint">{t("Oltre il terzo è quasi sempre l'intera rete.")}</p>
          </div>

          <div className="field" style={{ maxWidth: 180 }}>
            <label htmlFor="map-fanout">{t('Vicini per nodo')}</label>
            <input
              id="map-fanout"
              type="number"
              min={1}
              max={50}
              value={fanout}
              onChange={(e) => setFanout(Number(e.target.value))}
            />
            <p className="field__hint">{t('I più voluminosi. Massimo 50.')}</p>
          </div>

          <div className="field" style={{ maxWidth: 200 }}>
            <label htmlFor="map-window">{t('Finestra')}</label>
            <select
              id="map-window"
              value={window_}
              onChange={(e) => setWindow(e.target.value)}
            >
              {WINDOWS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label()}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button onClick={() => seed.trim() && explore(seed.trim())} disabled={!seed.trim()}>
          {t('Esplora')}
        </button>
      </Card>

      <ErrorBanner error={graph.error} />

      {graph.loading && <Spinner />}

      {data && data.nodes.length <= 1 && !graph.loading && (
        <Card>
          <EmptyState
            title={t('Nessuna conversazione in questa finestra')}
            hint={t("L'indirizzo non compare nel traffico osservato nel periodo scelto. Prova una finestra più ampia, o verifica che il filtro del traffico non lo stia scartando: in Impostazioni → Traffico da conservare.")}
          />
        </Card>
      )}

      {data && data.nodes.length > 1 && (
        <>
          {/* Ciò che la mappa non mostra si dice **sopra** la mappa, non sotto:
              sotto lo si legge dopo aver già tratto le conclusioni. */}
          {(data.nodes_with_more > 0 || data.stopped) && (
            <div className="banner banner--warn">
              {data.stopped && <strong>{data.stopped} </strong>}
              {data.nodes_with_more > 0 && (
                <>
                  {data.nodes_with_more === 1
                    ? t('1 nodo ha altri interlocutori oltre il tetto di {tetto} per nodo (segnati con ⁺). Quello che vedi è la parte più voluminosa del vicinato, non tutto il vicinato.', { tetto: fanout })
                    : t('{n} nodi hanno altri interlocutori oltre il tetto di {tetto} per nodo (segnati con ⁺). Quello che vedi è la parte più voluminosa del vicinato, non tutto il vicinato.', { n: data.nodes_with_more, tetto: fanout })}
                </>
              )}
            </div>
          )}

          <Card title={t('Attorno a {seed}', { seed: data.seed })}>
            {(esclusi.size > 0 || esclusiArchi.size > 0) && (
              <p style={{ marginTop: 0 }}>
                {t('Esclusi dalla mappa:')}{' '}
                {[...esclusi].map((addr) => (
                  <button
                    key={addr}
                    type="button"
                    className="chip"
                    style={{ cursor: 'pointer' }}
                    title={t('Riporta sulla mappa')}
                    onClick={() => ripristina(addr)}
                  >
                    {data.nodes.find((n) => n.address === addr)?.name ?? addr} ✕
                  </button>
                ))}
                {[...esclusiArchi].map((chiave) => (
                  <button
                    key={chiave}
                    type="button"
                    className="chip"
                    style={{ cursor: 'pointer' }}
                    title={t('Riporta sulla mappa')}
                    onClick={() => ripristinaArco(chiave)}
                  >
                    {chiave} ✕
                  </button>
                ))}
                <button
                  type="button"
                  className="linklike"
                  onClick={() => {
                    setEsclusi(new Set())
                    setEsclusiArchi(new Set())
                  }}
                >
                  {t('Ripristina tutti')}
                </button>
              </p>
            )}
            <Mappa
              nodi={nodiMappa}
              archi={archiMappa}
              layout={layout}
              onLayout={(l) => {
                setLayout(l)
                scriviLayout(l)
                // Le posizioni a mano appartengono alla vista in cui sono
                // state decise: cambiando vista le renderebbero casuali.
                setSpostati(new Map())
              }}
              nomiFasce={(livello) => (livello === 0 ? t('Seme') : t('Livello {n}', { n: livello }))}
              fissati={spostati}
              onFissa={(id, p) =>
                setSpostati((prima) => {
                  const dopo = new Map(prima)
                  dopo.set(id, p)
                  return dopo
                })
              }
              selezionato={selected}
              arcoScelto={selEdge}
              onClickNodo={(id, e) => {
                // Maiusc+clic toglie il nodo: è il gesto rapido per ripulire
                // la mappa prima di farne un servizio.
                if (e.shiftKey) escludi(id)
                else setSelected(id)
              }}
              onDoppioClickNodo={(id) => explore(id)}
              onMenuNodo={(id, x, y) => setMenu({ x, y, kind: 'nodo', chiave: id })}
              onClickArco={(id) => setSelEdge((sel) => (sel === id ? null : id))}
              onMenuArco={(id, x, y) => {
                setSelEdge(id)
                setMenu({ x, y, kind: 'arco', chiave: id })
              }}
              onClickSfondo={() => setSelEdge(null)}
              inquadra={inquadra}
              legenda={legendaMappa}
              etichetta={t('Mappa di correlazione di {seed}: {n} nodi', { seed: data.seed, n: visibili.nodes.length })}
            />

            {menu && (
              <div
                className="ctxmenu"
                style={{ left: menu.x, top: menu.y }}
                onClick={(e) => e.stopPropagation()}
              >
                {menu.kind === 'nodo' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setSelected(menu.chiave)
                        setMenu(null)
                      }}
                    >
                      {t('Dettaglio')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        explore(menu.chiave)
                      }}
                    >
                      {t('Esplora da qui')}
                    </button>
                    {menu.chiave !== data.seed && (
                      <button
                        type="button"
                        onClick={() => {
                          escludi(menu.chiave)
                          setMenu(null)
                        }}
                      >
                        {t('Escludi dalla mappa')}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        escludiArco(menu.chiave)
                        setMenu(null)
                      }}
                    >
                      {t('Escludi la comunicazione')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelEdge(null)
                        setMenu(null)
                      }}
                    >
                      {t('Deseleziona')}
                    </button>
                  </>
                )}
              </div>
            )}

            {selEdge && (
              <p style={{ marginBottom: 0 }}>
                {t('Comunicazione scelta:')} <span className="mono">{selEdge}</span>{' '}
                <button
                  type="button"
                  className="secondary"
                  onClick={() => escludiArco(selEdge)}
                >
                  {t('Escludi la comunicazione')}
                </button>{' '}
                <button type="button" className="linklike" onClick={() => setSelEdge(null)}>
                  {t('Deseleziona')}
                </button>
              </p>
            )}

            <p className="field__hint">
              {t('Un clic su un nodo mostra il dettaglio, un doppio clic riparte da lì, e un nodo si trascina dove serve. Un clic su una linea sceglie la comunicazione — qui e nella tabella sotto. Maiusc+clic esclude un nodo; rotella per ingrandire, trascinamento della tela per spostare.')}{' '}
              {(() => {
                // Una frase sola, con la parola colorata al posto del segnaposto.
                const [prima, dopo = ''] = t('In {giallo} gli indirizzi che la CMDB non conosce: non è un difetto della mappa, è la risposta — e trovarli è uno dei motivi per cui questo traffico si raccoglie.').split('{giallo}')
                return (
                  <>
                    {prima}
                    <span style={{ color: 'var(--warn)' }}>{t('giallo')}</span>
                    {dopo}
                  </>
                )
              })()}
            </p>
          </Card>

          {/* La tabella delle comunicazioni: la stessa mappa, riga per riga.
              Vanno di pari passo per costruzione — la selezione è UNO stato
              condiviso — così cliccare una linea evidenzia la riga, cliccare la
              riga evidenzia la linea, e nessuna delle due viste può mentire
              sull'altra. */}
          <Card title={t('Comunicazioni ({n})', { n: visibili.edges.length })}>
            {spuntate.size > 0 && (
              <p style={{ marginTop: 0 }}>
                {t('{quante} comunicazioni selezionate.', { quante: spuntate.size })}{' '}
                <button type="button" className="secondary" onClick={escludiSpuntate}>
                  {t('Escludi le selezionate')}
                </button>{' '}
                <button
                  type="button"
                  className="linklike"
                  onClick={() => setSpuntate(new Set())}
                >
                  {t('Annulla la selezione')}
                </button>
              </p>
            )}
            <DataTable
              rows={visibili.edges}
              columns={colonneComunicazioni}
              rowKey={(edge, i) => `${chiaveArco(edge)}-${i}`}
              initialSort={{ key: 'bytes', dir: 'desc' }}
              conteggio={{ nome: t('comunicazioni') }}
              selezione={{ chiavi: spuntate, onCambia: setSpuntate, id: chiaveArco }}
              onRowClick={(edge) => {
                const chiave = chiaveArco(edge)
                setSelEdge(selEdge === chiave ? null : chiave)
              }}
              isSelected={(edge) => selEdge === chiaveArco(edge)}
            />
          </Card>

          {detail && (
            <Card
              title={detail.name ?? detail.address}
              actions={
                detail.level !== 0 ? (
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => escludi(detail.address)}
                  >
                    {t('Escludi dalla mappa')}
                  </button>
                ) : undefined
              }
            >
              <div className="stats">
                <div className="stat">
                  <div className="stat__value">{detail.address}</div>
                  <div className="stat__label">{t('Indirizzo')}</div>
                </div>
                <div className="stat">
                  <div className="stat__value">{detail.level}</div>
                  <div className="stat__label">{t('Distanza dal seme')}</div>
                </div>
                <div className="stat">
                  <div className="stat__value">{detailEdges.length}</div>
                  <div className="stat__label">{t('Archi mostrati')}</div>
                </div>
              </div>

              {!detail.expanded && (
                // «Non ha vicini» e «i suoi vicini non sono stati cercati» sono
                // due cose diverse: senza dirlo, l'ultimo livello sembra un
                // confine della rete invece che il confine della domanda.
                <div className="banner banner--info">
                  {t('I vicini di questo nodo non sono stati cercati: è al bordo dei livelli richiesti. Doppio clic per ripartire da qui.')}
                </div>
              )}

              <DataTable
                rows={detailEdges}
                columns={colonneDettaglio}
                rowKey={(edge, i) => `${interlocutore(edge)}-${edge.dst_port}-${i}`}
                initialSort={{ key: 'bytes', dir: 'desc' }}
                conteggio={{ nome: t('comunicazioni') }}
              />
            </Card>
          )}
          <CreaServizio
            seed={data.seed}
            nodi={visibili.nodes}
            archi={visibili.edges}
            finestra={query?.window ?? window_}
            livelli={query?.levels ?? levels}
            esclusi={esclusi.size}
          />
        </>
      )}
      </>
      )}
    </>
  )
}

/**
 * Da una mappa ripulita a un business service.
 *
 * ## Perché da qui
 *
 * Perché la mappa **è** il lavoro: si parte da un indirizzo, si esplora, si
 * escludono il DNS, il monitoraggio e il backup che parlano con tutti — e quello
 * che resta è il servizio. Rifare quell'elenco a mano nella pagina dei servizi
 * significherebbe trascrivere ciò che si ha già davanti, un ingresso alla
 * volta, sbagliando un indirizzo su dieci.
 *
 * ## Cosa diventa un ingresso
 *
 * Ogni nodo visibile, con l'indirizzo, il CI quando la CMDB lo conosce, e la
 * porta su cui riceve più traffico — è la porta «del servizio», e le altre
 * restano leggibili dalla mappa del servizio stesso. Il servizio creato è come
 * ogni altro: si apre in Business service e si modifica quando si vuole.
 */
function CreaServizio({
  seed,
  nodi,
  archi,
  finestra,
  livelli,
  esclusi,
}: {
  seed: string
  nodi: GraphNode[]
  archi: GraphEdge[]
  finestra: string
  livelli: number
  esclusi: number
}) {
  const [nome, setNome] = useState('')
  const [criticita, setCriticita] = useState('medium')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [creato, setCreato] = useState<{ id: string; name: string } | null>(null)

  // La porta principale di ogni nodo: quella su cui riceve più byte. Le altre
  // non si perdono — il servizio ricostruisce la propria mappa dal traffico —
  // ma l'ingresso dichiarato deve nominarne una, e sia quella che pesa.
  const portaDi = useMemo(() => {
    const pesi = new Map<string, Map<number, number>>()
    for (const a of archi) {
      const per = pesi.get(a.dst) ?? new Map<number, number>()
      per.set(a.dst_port, (per.get(a.dst_port) ?? 0) + a.bytes)
      pesi.set(a.dst, per)
    }
    const fuori = new Map<string, number | null>()
    for (const [addr, per] of pesi) {
      let migliore: number | null = null
      let massimo = -1
      for (const [porta, byte] of per) {
        if (byte > massimo) {
          massimo = byte
          migliore = porta
        }
      }
      fuori.set(addr, migliore)
    }
    return fuori
  }, [archi])

  async function crea() {
    setBusy(true)
    setErrore(null)
    try {
      const servizio = await api.createService({
        name: nome.trim(),
        description: t('Creato dalla mappa di correlazione di {seed} (finestra {finestra})', {
          seed,
          finestra,
        }),
        criticality: criticita,
        max_depth: livelli,
        entries: nodi.map((n) => ({
          ci_id: n.ci_id,
          address: n.address,
          port: portaDi.get(n.address) ?? null,
          protocol: null,
          // L'etichetta è il nome con cui il nodo si legge sulla mappa: chi
          // aprirà il servizio fra sei mesi deve riconoscere gli ingressi
          // senza risolvere indirizzi a memoria.
          label: n.name,
        })),
      })
      setCreato({ id: servizio.id, name: servizio.name })
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  // Il limite del servizio: oltre trentadue ingressi la creazione verrebbe
  // rifiutata dal server. Dirlo prima — con la via d'uscita — invece di
  // lasciare che il modulo compilato fallisca dopo.
  const MASSIMO_INGRESSI = 32
  const troppi = nodi.length > MASSIMO_INGRESSI

  if (creato) {
    return (
      <Card title={t('Business service creato')}>
        <p style={{ margin: 0 }}>
          <strong>{creato.name}</strong> — {t('{n} ingressi dalla mappa.', { n: nodi.length })}{' '}
          <Link to="/services">{t('Aprilo in Business service per modificarlo →')}</Link>
        </p>
      </Card>
    )
  }

  return (
    <Card title={t('Crea un business service da questa mappa')}>
      <p style={{ marginTop: 0 }} className="field__hint">
        {t('Gli ingressi saranno i {quanti} nodi visibili, ognuno con il suo indirizzo e la porta su cui riceve più traffico.', { quanti: nodi.length })}
        {esclusi > 0 && <> {t('I {esclusi} nodi esclusi restano fuori.', { esclusi })}</>}{' '}
        {t('Il servizio resta modificabile in ogni momento dalla pagina Business service.')}
      </p>
      <ErrorBanner error={errore} />
      {troppi && (
        <div className="banner banner--warn">
          {t('La mappa ha {quanti} nodi e un servizio ne accetta al massimo {massimo}: escludi quelli che non c’entrano (Maiusc+clic), o riduci livelli e vicini per nodo.', { quanti: nodi.length, massimo: MASSIMO_INGRESSI })}
        </div>
      )}
      <div className="row">
        <div className="field">
          <label htmlFor="bs-nome">{t('Nome')}</label>
          <input
            id="bs-nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder={t('es. Servizio attorno a {seed}', { seed })}
          />
        </div>
        <div className="field" style={{ maxWidth: 220 }}>
          <label htmlFor="bs-criticita">{t('Criticità')}</label>
          <select
            id="bs-criticita"
            value={criticita}
            onChange={(e) => setCriticita(e.target.value)}
          >
            <option value="critical">{t('Critica')}</option>
            <option value="high">{t('Alta')}</option>
            <option value="medium">{t('Media')}</option>
            <option value="low">{t('Bassa')}</option>
          </select>
        </div>
      </div>
      <button onClick={crea} disabled={busy || !nome.trim() || troppi}>
        {busy ? t('Creazione…') : t('Crea il business service')}
      </button>
    </Card>
  )
}

